import React, { useState } from 'react';
import classnames from 'classnames';
import { ControlledModal } from '@instinet-analytics/semantic-ui-components';
import ShareLayoutContent from './ShareLayoutContent';
import LayoutListContent from './LayoutListContent';
import DuplicateLayoutContent from './DuplicateLayoutContent';
import DeleteLayoutContent from './DeleteLayoutContent';
import { useMakeRequest } from 'utils/hooks/request';
import {
    deleteLayoutAction,
    duplicateLayoutAction,
    shareLayoutAction,
} from './store/thunks';
import ManagedLayout from './ManagedLayout';
import { useLayoutSelector } from 'layout/store/selectors';
import { IconNames } from '@instinet-analytics/semantic-ui-components';

import { useInputError, textUtils } from '@instinet-analytics/core-lib';
import './LayoutManagerModal.scss';
import {
    useDispatchShowInfo,
    useDispatchShowWarning,
} from 'notification/store/dispatcher';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { isLayoutNameValid } from './layoutNameValidator';
import {
    useAppLayoutOrigin,
    WellKnownLayoutOrigin,
} from 'layout/WellKnownLayoutOrigin';
import { useDispatchLoadShareUsers } from 'app/store/shareUsers/dispatcher';
import { useDispatchSwitchLayoutAction } from 'layout/store/dispatcher';
import {
    defaultDictionary,
    LayoutManagerDictionary,
} from 'layoutManagement/LayoutManagerDictionary';
import SaveCurrentLayoutAsContent from 'layoutManagement/SaveCurrentLayoutAsContent';
import { useLayoutsList } from 'layout/store/useLayoutsList';

const { isFalsyOrWhitespace } = textUtils;

enum ModalContent {
    LayoutList,
    DuplicateLayout,
    ShareLayout,
    DeleteLayout,
    SaveLayoutAs,
}

export interface LayoutManagerModalProps {
    className?: string;
    origin?: string;
    isOpen: boolean;
    dictionary?: Partial<LayoutManagerDictionary>;
    loadIcon?: IconNames;
    loadHoverIcon?: IconNames;
    /*
     * For widgets we always show the globals
     * For standalone and dashboards, when the user has their own copy, we only show that one
     */
    showGlobalsWhenDuplicatedInUser?: boolean;
    onActionCompleted: () => void;
    onSaveCurrent?: (name: string, id: number | null) => Promise<void>;
    onLoadLayout?: (name: string, globalVersion?: boolean) => Promise<void>;
}

export default function LayoutManagerModal(props: LayoutManagerModalProps) {
    const {
        className,
        dictionary: propsDictionary,
        isOpen,
        onActionCompleted,
        origin: originProps,
        loadIcon,
        loadHoverIcon,
        showGlobalsWhenDuplicatedInUser,
        onSaveCurrent,
        onLoadLayout,
    } = props;

    const [currentContent, setCurrentContent] = useState(
        ModalContent.LayoutList
    );
    const [actionLayout, setActionLayout] = useState<ManagedLayout>();
    const [duplicateLayoutName, setDuplicateLayoutName] = useState('');
    const [saveAsLayoutName, setSaveAsLayoutName] = useState('');
    const [shareLayoutUsername, setShareLayoutUsername] = useState('');

    const dictionary: LayoutManagerDictionary = {
        ...defaultDictionary,
        ...propsDictionary,
    };

    const activeLayout = useLayoutSelector();

    const appLayoutOrigin = useAppLayoutOrigin();
    const origin = originProps ? originProps : appLayoutOrigin;
    const { layoutList, forceLayoutListReload } = useLayoutsList(origin);

    const isAppLayoutManager =
        origin === WellKnownLayoutOrigin.STANDALONE ||
        origin === WellKnownLayoutOrigin.PALETTE;

    const makeRequest = useMakeRequest();

    const dispatchSwitchLayout = useDispatchSwitchLayoutAction();

    const dispatchShowWarning = useDispatchShowWarning();
    const dispatchShowInfo = useDispatchShowInfo();

    const dispatchLoadShareUsers = useDispatchLoadShareUsers();

    const shareLayoutFeatureToggleEnabled = useSelectIsFeatureEnabled(
        'layoutManager.layoutSharing'
    );

    const {
        errorMessage,
        invalidInput,
        setInputTextAndClearInvalidState,
        clearError,
        displayError,
    } = useInputError();

    if (!activeLayout) {
        return null;
    }

    const returnToMainContent = () => {
        setCurrentContent(ModalContent.LayoutList);
        clearError();
    };

    const onLoadLayoutClicked = (layout: ManagedLayout) => {
        if (onLoadLayout) {
            onLoadLayout(layout.name, layout.isGlobal);
            return;
        }
        if (layout.layoutId !== activeLayout.id) {
            dispatchSwitchLayout(origin, layout.name);
        }
    };

    const onShareLayoutClicked = (layout: ManagedLayout) => {
        setActionLayout(layout);
        dispatchLoadShareUsers();
        setCurrentContent(ModalContent.ShareLayout);
    };

    const onDuplicateLayoutClicked = (layout: ManagedLayout) => {
        setActionLayout(layout);
        setCurrentContent(ModalContent.DuplicateLayout);
    };

    const onSaveCurrentClick = onSaveCurrent
        ? () => {
              setCurrentContent(ModalContent.SaveLayoutAs);
          }
        : undefined;

    const onDeleteLayoutClicked = (layout: ManagedLayout) => {
        setActionLayout(layout);
        setCurrentContent(ModalContent.DeleteLayout);
    };
    const onDeleteLayoutConfirmClicked = () => {
        const deletingActive = actionLayout!.layoutId === activeLayout.id;

        makeRequest<[number], void>(deleteLayoutAction)(actionLayout!.layoutId)
            .then(() => {
                if (deletingActive) {
                    dispatchSwitchLayout(origin, 'default');
                }
                forceLayoutListReload();
                returnToMainContent();
            })
            .catch(() => {
                dispatchShowWarning('Error deleting layout');
            });
    };

    const duplicateLayout = () => {
        const layoutToDuplicate = actionLayout;
        if (!layoutToDuplicate) {
            setDuplicateLayoutName('');
            displayError('Please enter a layout name');
            return;
        }
        const layoutNameValidation = isLayoutNameValid(duplicateLayoutName);
        if (!layoutNameValidation.isValid) {
            displayError(layoutNameValidation.message!);
            return;
        }
        makeRequest<[number, string], void>(duplicateLayoutAction)(
            layoutToDuplicate.layoutId,
            duplicateLayoutName
        )
            .then(() => {
                dispatchSwitchLayout(activeLayout.origin, duplicateLayoutName);
                setDuplicateLayoutName('');
                forceLayoutListReload();
                returnToMainContent();
            })
            .catch((err) => {
                displayError('Error duplicating layout', err);
            });
    };

    const saveAsLayout = () => {
        const layoutNameValidation = isLayoutNameValid(saveAsLayoutName);
        if (!layoutNameValidation.isValid) {
            displayError(layoutNameValidation.message!);
            return;
        }

        const id = layoutList.find(
            (r) => r.name === saveAsLayoutName && !r.isGlobal
        )?.layoutId;
        onSaveCurrent!(saveAsLayoutName, id ? id : null)
            .then(() => {
                setSaveAsLayoutName('');
                forceLayoutListReload();
                returnToMainContent();
            })
            .catch((err) => {
                displayError('Error saving layout', err);
            });
    };

    const shareLayout = () => {
        if (isFalsyOrWhitespace(shareLayoutUsername)) {
            setShareLayoutUsername('');
            displayError('Please enter a username');
            return;
        }

        clearError();

        makeRequest<[number, string], void>(shareLayoutAction)(
            actionLayout!.layoutId,
            shareLayoutUsername

            // TODO: support sharing with multiple users at once
        )
            .then(() => {
                dispatchShowInfo(`Layout shared with ${shareLayoutUsername}`);
                forceLayoutListReload();
                returnToMainContent();
            })
            .catch((err) => {
                displayError('Error sharing layout', err);
            })
            .finally(() => {
                setShareLayoutUsername('');
            });
    };

    const getModalContentProps = (currentContent: ModalContent) => {
        const defaultProps = { cancelButtonText: 'Back' };

        switch (currentContent) {
            case ModalContent.LayoutList:
                return {
                    ...defaultProps,
                    primaryButtonText: 'Done',
                    modalClassName: 'main',
                    onPrimaryClick: onActionCompleted,
                };
            case ModalContent.ShareLayout:
                return {
                    ...defaultProps,
                    primaryButtonText: 'Share Layout',
                    modalClassName: 'share',
                    onPrimaryClick: shareLayout,
                };
            case ModalContent.DuplicateLayout:
                return {
                    ...defaultProps,
                    primaryButtonText: 'Create Layout',
                    modalClassName: 'duplicate',
                    onPrimaryClick: duplicateLayout,
                };
            case ModalContent.SaveLayoutAs:
                return {
                    ...defaultProps,
                    primaryButtonText: 'Save',
                    modalClassName: 'saveAs',
                    onPrimaryClick: saveAsLayout,
                };
            case ModalContent.DeleteLayout:
                return {
                    ...defaultProps,
                    primaryButtonText: 'Delete Layout',
                    modalClassName: 'delete',
                    cancelButtonText: 'Cancel',
                    onPrimaryClick: onDeleteLayoutConfirmClicked,
                };
        }
    };

    const modalContentProps = getModalContentProps(currentContent);

    return (
        <ControlledModal
            className={classnames(
                'LayoutManagerModal',
                modalContentProps.modalClassName,
                shareLayoutFeatureToggleEnabled
                    ? undefined
                    : 'sharing-disabled',
                className
            )}
            headerText={dictionary.headerText}
            isOpen={isOpen}
            saveText={modalContentProps.primaryButtonText}
            cancelText={modalContentProps.cancelButtonText}
            onCancel={
                currentContent !== ModalContent.LayoutList
                    ? returnToMainContent
                    : undefined
            }
            onSave={modalContentProps.onPrimaryClick}
        >
            {(() => {
                switch (currentContent) {
                    case ModalContent.LayoutList:
                        return (
                            <LayoutListContent
                                dictionary={dictionary}
                                shareLayoutFeatureToggleEnabled={
                                    shareLayoutFeatureToggleEnabled
                                }
                                layouts={layoutList}
                                loadIcon={loadIcon}
                                loadHoverIcon={loadHoverIcon}
                                activeLayoutId={activeLayout.id!}
                                activeLayoutName={activeLayout.name}
                                showGlobalsWhenDuplicatedInUser={
                                    showGlobalsWhenDuplicatedInUser
                                }
                                isAppLayoutManager={isAppLayoutManager}
                                onLoadClick={onLoadLayoutClicked}
                                onShareClick={onShareLayoutClicked}
                                onDuplicateClick={onDuplicateLayoutClicked}
                                onDeleteClick={onDeleteLayoutClicked}
                                onSaveCurrentClick={onSaveCurrentClick}
                            />
                        );

                    case ModalContent.ShareLayout:
                        return (
                            <ShareLayoutContent
                                layoutName={actionLayout!.name}
                                username={shareLayoutUsername}
                                onUsernameChanged={setInputTextAndClearInvalidState(
                                    setShareLayoutUsername
                                )}
                                error={errorMessage}
                                isInvalid={invalidInput}
                                onEnter={modalContentProps.onPrimaryClick}
                            />
                        );

                    case ModalContent.DuplicateLayout:
                        return (
                            <DuplicateLayoutContent
                                layoutName={actionLayout!.name}
                                duplicateName={duplicateLayoutName}
                                onDuplicateNameChanged={setInputTextAndClearInvalidState(
                                    setDuplicateLayoutName
                                )}
                                error={errorMessage}
                                isInvalid={invalidInput}
                                onEnter={modalContentProps.onPrimaryClick}
                            />
                        );

                    case ModalContent.SaveLayoutAs:
                        return (
                            <SaveCurrentLayoutAsContent
                                saveCurrentLayoutAsName={saveAsLayoutName}
                                onSaveCurrentLayoutAsNameChanged={setInputTextAndClearInvalidState(
                                    setSaveAsLayoutName
                                )}
                                error={errorMessage}
                                isInvalid={invalidInput}
                                onEnter={modalContentProps.onPrimaryClick}
                                dictionary={dictionary}
                            />
                        );

                    case ModalContent.DeleteLayout:
                        return (
                            <DeleteLayoutContent
                                shareLayoutFeatureToggleEnabled={
                                    shareLayoutFeatureToggleEnabled
                                }
                                layoutName={actionLayout!.name}
                            />
                        );

                    default:
                        return null;
                }
            })()}
        </ControlledModal>
    );
}
